// Apolo IDE — useTurnRouter (Turn Role Router, E4 / WS 4.1).
//
// Renderer-side glue for the per-turn role router. Owns two things:
//   1. the toggle (localStorage `apolo_turn_router`) — DEFAULT ON since the
//      code-tab-to-10 sprint: a fresh install gets automatic per-turn model
//      routing (chat/analysis → cheap model, build → default, risky → ask).
//      Setting '0'/'false' is the kill-switch (restores pre-sprint behavior);
//   2. `lastDecision` — the most recent routing decision the TurnRoleIndicator
//      renders (transparency: which role + why + which model, like Cursor's mode).
//
// FRAMEWORK-LIGHT BY CONTRACT: imports NOTHING from Electron and does NOT import
// the registry. The pure router (`routeTurn`) + resolver are CALLED at the
// composition root (SessionWorkspace); this hook only carries the toggle + the
// view state. When the toggle is off the composition root never routes, so the
// decision is null and the indicator renders nothing.

import { useCallback, useEffect, useState } from 'react';

export const LS_TURN_ROUTER = 'apolo_turn_router';

// DEFAULT ON: an ABSENT flag — or anything other than '0'/'false' — means ENABLED,
// so a fresh install gets automatic turn routing out of the box (like Cursor's
// "Ask vs Agent" mode selection). Setting '0'/'false' is the kill-switch.
export function readTurnRouterEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(LS_TURN_ROUTER);
    if (raw === null || raw === undefined) return true;
    return !(raw === '0' || raw === 'false');
  } catch {
    return true;
  }
}

export function writeTurnRouterEnabled(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_TURN_ROUTER, value ? '1' : '0');
  } catch {
    // ignore — private mode / quota
  }
}

export function useTurnRouter() {
  const [enabled, setEnabled] = useState(readTurnRouterEnabled);
  // The latest decision { role, reason, readOnly, permissionsMode, modelTier, modelId }
  // | null. null (or role 'none') ⇒ the indicator renders nothing.
  const [lastDecision, setLastDecision] = useState(null);

  // Mirror the toggle into localStorage on change.
  useEffect(() => {
    writeTurnRouterEnabled(enabled);
  }, [enabled]);

  const toggle = useCallback(() => setEnabled((v) => !v), []);

  return { enabled, toggle, lastDecision, setLastDecision };
}

export default useTurnRouter;
